import AddNewParameterModal from 'pages/InternalAdmin/organization/pages/Exports/AddNewParameterModal';
import EditParameterModal from 'pages/InternalAdmin/organization/pages/Exports/EditParameterModal';
import PermissionService from 'components/Common/PermissionService';
import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { productPerm } from 'utils/appConstants';
import { useCreateExportParameters } from 'api/internalAdmin/exports/useCreateExportParameter';
import { useDeleteExportParameter } from 'api/internalAdmin/exports/useDeleteExportParameter';
import { useEditExportParameters } from 'api/internalAdmin/exports/useEditExportParameters';
import { useFetchExportParameters } from 'api/internalAdmin/exports/useFetchExportParameters';


const ExportParametersTable = () => {
  const isFeatureFlagEnabled = PermissionService.isProductEnabled(
    [productPerm.EnableCustomExportPath]);

  const {
    data = [], isLoading, isError, isSuccess, isFetching, refetch,
  } = useFetchExportParameters();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editParam, setEditParam] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [paramToDelete, setParamToDelete] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const { mutate: editExportParam } = useEditExportParameters();
  const { mutate: deleteExportParam, isLoading: isDeleteLoading } = useDeleteExportParameter();
  const { mutate: createExportParam } = useCreateExportParameters();

  const ORGANIZATION_ID = '<YOUR_ORG_ID>'; // Replace with actual org ID

  const handleEdit = (account, param) => {
    setEditAccount(account);
    setEditParam(param);
    setIsEditModalOpen(true);
  };

  const handleRemove = (account, param) => {
    setAccountToDelete(account);
    setParamToDelete(param);
    setIsDeleteConfirmOpen(true);
  };

  const handleAddNew = () => setIsAddModalOpen(true);
  const handleAddModalClose = () => setIsAddModalOpen(false);
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditAccount(null);
    setEditParam(null);
  };

  const handleSaveNewParameter = (newParam) => {
    const {
      accountId, name, value,
    } = newParam;

    createExportParam({
      organizationID: ORGANIZATION_ID,
      partnerAccountID: accountId,
      parameters: {
        partnerAccountId: accountId,
        name,
        value,
      },
    }, {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refetch();
      },
    });
  };

  const handleSaveEdit = (updatedParam) => {
    if (!editAccount) return;

    const partnerAccountId = updatedParam.ID;
    editExportParam({
      partnerAccountID: partnerAccountId,
      parameters: {
        partnerAccountId,
        name: updatedParam.name,
        value: updatedParam.value,
      },
    }, {
      onSuccess: () => {
        setIsEditModalOpen(false);
        refetch();
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!accountToDelete || !paramToDelete) return;

    const parameterID = paramToDelete.ID;

    if (!parameterID) {
      setIsDeleteConfirmOpen(false);
      return;
    }

    deleteExportParam({
      partnerAccountID: paramToDelete.partnerAccountId,
      parameterID,
    }, {
      onSuccess: () => {
        refetch();
        setIsDeleteConfirmOpen(false);
      },
      onError: () => {
        setIsDeleteConfirmOpen(false);
      },
    });
  };

  if (!isFeatureFlagEnabled) {
    return (
      <Box display='flex' justifyContent='center' mt={4}>
        <Typography color='error'>
          &quot;Allow Custom Export Path&quot; Feature Flag needs to be enabled to
          access this feature.
        </Typography>
      </Box>
    );
  }

  if (isLoading || isFetching) {
    return (
      <Box display='flex' justifyContent='center' mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display='flex' justifyContent='center' mt={4}>
        <Typography color='error'>Error loading export parameters</Typography>
      </Box>
    );
  }

  const filteredData = data.filter(account => account.parameters && account.parameters.length > 0);

  const partners = [
    ...new Map(
      data
        .filter(item => item.partnerId && item.partnerName)
        .map(item => [item.partnerId, { id: item.partnerId, name: item.partnerName }]),
    ).values(),
  ];

  const accounts = data.map(account => ({
    id: account.id,
    name: account.name,
    partnerId: account.partnerId,
  }));

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end' mb={1}>
        <Button variant='contained' color='primary' onClick={handleAddNew}>
          Add New
        </Button>
      </Box>

      {isSuccess && filteredData.length === 0 ? (
        <>
          <Box display='flex' justifyContent='center' mt={4}>
            <Typography>No partner accounts with export parameters found.</Typography>
          </Box>

          <AddNewParameterModal
            open={isAddModalOpen}
            onClose={handleAddModalClose}
            onSave={handleSaveNewParameter}
            partners={partners}
            accounts={accounts}
          />
        </>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Export Type</TableCell>
                  <TableCell>Partner Account ID</TableCell>
                  <TableCell>Destination Name</TableCell>
                  <TableCell>Parameter Name</TableCell>
                  <TableCell>Parameter Value</TableCell>
                  <TableCell align='center'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map(account => account.parameters.map(param => (
                  <TableRow key={`${account.ID}-${param.ID}`}>
                    <TableCell>{account.partnerName}</TableCell>
                    <TableCell>{account.id}</TableCell>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>{param.name}</TableCell>
                    <TableCell>{param.value}</TableCell>
                    <TableCell align='center'>
                      <Box display='flex' gap={1} justifyContent='center'>
                        <Button
                          size='small'
                          variant='contained'
                          color='primary'
                          onClick={() => handleEdit(account, param)}
                        >
                          Edit
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          color='error'
                          onClick={() => handleRemove(account, param)}
                          disabled={isDeleteLoading}
                        >
                          Remove
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )))}
              </TableBody>
            </Table>
          </TableContainer>

          <AddNewParameterModal
            open={isAddModalOpen}
            onClose={handleAddModalClose}
            onSave={handleSaveNewParameter}
            partners={partners}
            accounts={accounts}
          />
        </>
      )}

      {isEditModalOpen && editAccount && editParam && (
        <EditParameterModal
          open={isEditModalOpen}
          onClose={handleEditModalClose}
          onSave={handleSaveEdit}
          parameterData={{
            partnerName: editAccount.partnerName,
            accountName: editAccount.name,
            ID: editParam.partnerAccountId,
            name: editParam.name,
            value: editParam.value,
          }}
        />
      )}

      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete parameter
            {' '}
            <strong>{paramToDelete?.name}</strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)} variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            disabled={isDeleteLoading}
          >
            {isDeleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportParametersTable;
